import {
  Bell,
  Building2,
  Calendar,
  ClipboardList,
  Flag,
  Home,
  Leaf,
  List,
  type LucideIcon,
  Mail,
  QrCode,
  ShoppingBag,
  Star,
  Tags,
  Truck,
  User,
  UserPlus,
  Users,
  UtensilsCrossed,
} from 'lucide-react';

import { UserRole } from '@/lib/enums';

export type NavItemId =
  | 'home'
  | 'menuSelection'
  | 'orders'
  | 'menuManagement'
  | 'menuPlanner'
  | 'dishes'
  | 'mealRatings'
  | 'dishCategories'
  | 'distribution'
  | 'employeeManagement'
  | 'employeeManagementAll'
  | 'employeeManagementCreate'
  | 'employeeManagementBulk'
  | 'employeeOrders'
  | 'companies'
  | 'canteens'
  | 'notifications'
  | 'profile'
  | 'reportIssues'
  | 'qrCode'
  | 'foodWaste';

/**
 * Information-architecture groups shared by the desktop sidebar and the
 * mobile More sheet. Group labels live under `navigation.appSidebar.groups.*`
 * (sidebar) and `common.navigation.groups.*` (More sheet).
 */
export type NavGroupKey = 'overview' | 'dailyOperations' | 'administration';

export interface NavItemDef {
  id: NavItemId;
  href: string;
  icon: LucideIcon;
  /** Translation key under `navigation.appSidebar.*` (or `navigation.appSidebar.subItems.*` for sub-items). */
  sidebarLabelKey?: string;
  /** Translation key under `common.navigation.*`. */
  bottomNavLabelKey?: string;
  /**
   * Group the item is bucketed under in the mobile More sheet. May differ
   * from the item's sidebar placement (e.g. `employeeOrders` sits under
   * Employee Management in the sidebar but among daily operations on mobile).
   * Items that never appear in the More sheet omit it.
   */
  moreMenuGroup?: NavGroupKey;
  roles: readonly UserRole[];
}

const ALL_ROLES = [UserRole.admin, UserRole.kitchen, UserRole.employee] as const;

export const NAV_ITEMS: Record<NavItemId, NavItemDef> = {
  home: {
    id: 'home',
    href: '/app',
    icon: Home,
    sidebarLabelKey: 'dashboard',
    bottomNavLabelKey: 'home',
    roles: ALL_ROLES,
  },
  menuSelection: {
    id: 'menuSelection',
    href: '/app/menu-selection',
    icon: ClipboardList,
    sidebarLabelKey: 'menuSelection',
    bottomNavLabelKey: 'menu',
    moreMenuGroup: 'dailyOperations',
    roles: ALL_ROLES,
  },
  orders: {
    id: 'orders',
    href: '/app/orders',
    icon: ShoppingBag,
    sidebarLabelKey: 'orders',
    bottomNavLabelKey: 'orders',
    moreMenuGroup: 'dailyOperations',
    roles: [UserRole.employee, UserRole.kitchen],
  },
  menuManagement: {
    id: 'menuManagement',
    href: '/app/menu-management/planner',
    icon: UtensilsCrossed,
    sidebarLabelKey: 'menuManagement',
    roles: [UserRole.admin, UserRole.kitchen],
  },
  menuPlanner: {
    id: 'menuPlanner',
    href: '/app/menu-management/planner',
    icon: Calendar,
    sidebarLabelKey: 'subItems.weeklyPlanner',
    bottomNavLabelKey: 'menuPlanner',
    moreMenuGroup: 'dailyOperations',
    roles: [UserRole.admin, UserRole.kitchen],
  },
  dishes: {
    id: 'dishes',
    href: '/app/menu-management/dishes',
    icon: List,
    sidebarLabelKey: 'subItems.dishes',
    bottomNavLabelKey: 'dishes',
    moreMenuGroup: 'dailyOperations',
    roles: [UserRole.admin, UserRole.kitchen],
  },
  mealRatings: {
    id: 'mealRatings',
    href: '/app/menu-management/ratings',
    icon: Star,
    sidebarLabelKey: 'subItems.mealRatings',
    bottomNavLabelKey: 'mealRatings',
    moreMenuGroup: 'dailyOperations',
    roles: [UserRole.admin, UserRole.kitchen],
  },
  dishCategories: {
    id: 'dishCategories',
    href: '/app/dish-categories',
    icon: Tags,
    sidebarLabelKey: 'subItems.dishCategories',
    bottomNavLabelKey: 'dishCategories',
    moreMenuGroup: 'dailyOperations',
    roles: [UserRole.admin, UserRole.kitchen],
  },
  distribution: {
    id: 'distribution',
    href: '/app/distribution',
    icon: Truck,
    sidebarLabelKey: 'distribution',
    bottomNavLabelKey: 'distribution',
    moreMenuGroup: 'dailyOperations',
    roles: [UserRole.admin, UserRole.kitchen],
  },
  employeeManagement: {
    id: 'employeeManagement',
    href: '/app/employee-management',
    icon: Users,
    sidebarLabelKey: 'employeeManagement',
    bottomNavLabelKey: 'users',
    moreMenuGroup: 'administration',
    roles: [UserRole.admin],
  },
  employeeManagementAll: {
    id: 'employeeManagementAll',
    href: '/app/employee-management',
    icon: Users,
    sidebarLabelKey: 'subItems.allEmployees',
    roles: [UserRole.admin],
  },
  employeeManagementCreate: {
    id: 'employeeManagementCreate',
    href: '/app/employee-management/create',
    icon: UserPlus,
    sidebarLabelKey: 'subItems.createEmployee',
    roles: [UserRole.admin],
  },
  employeeManagementBulk: {
    id: 'employeeManagementBulk',
    href: '/app/employee-management/bulk-create',
    icon: Mail,
    sidebarLabelKey: 'subItems.bulkCreate',
    roles: [UserRole.admin],
  },
  employeeOrders: {
    id: 'employeeOrders',
    href: '/app/employee-management/orders',
    icon: ClipboardList,
    sidebarLabelKey: 'subItems.employeeOrders',
    bottomNavLabelKey: 'employeeOrders',
    moreMenuGroup: 'dailyOperations',
    roles: [UserRole.admin],
  },
  companies: {
    id: 'companies',
    href: '/app/companies',
    icon: Building2,
    sidebarLabelKey: 'companyManagement',
    bottomNavLabelKey: 'companies',
    moreMenuGroup: 'administration',
    roles: [UserRole.admin],
  },
  canteens: {
    id: 'canteens',
    href: '/app/canteens',
    icon: UtensilsCrossed,
    sidebarLabelKey: 'canteenManagement',
    bottomNavLabelKey: 'canteens',
    moreMenuGroup: 'administration',
    roles: [UserRole.admin],
  },
  notifications: {
    id: 'notifications',
    href: '/app/notifications',
    icon: Bell,
    sidebarLabelKey: 'notifications',
    bottomNavLabelKey: 'notifications',
    moreMenuGroup: 'overview',
    roles: ALL_ROLES,
  },
  profile: {
    id: 'profile',
    href: '/app/profile',
    icon: User,
    bottomNavLabelKey: 'profile',
    moreMenuGroup: 'overview',
    roles: ALL_ROLES,
  },
  reportIssues: {
    id: 'reportIssues',
    href: '/app/report-issues',
    icon: Flag,
    sidebarLabelKey: 'reportIssues',
    bottomNavLabelKey: 'reportIssues',
    // Reporting an issue is open to every role, so it lives in Overview —
    // an "Administration" header over it reads wrong for employees.
    moreMenuGroup: 'overview',
    roles: ALL_ROLES,
  },
  qrCode: {
    id: 'qrCode',
    href: '/app/qr-code',
    icon: QrCode,
    bottomNavLabelKey: 'qrCode',
    roles: ALL_ROLES,
  },
  foodWaste: {
    id: 'foodWaste',
    href: '/app/food-waste',
    icon: Leaf,
    sidebarLabelKey: 'foodWaste',
    bottomNavLabelKey: 'foodWaste',
    moreMenuGroup: 'overview',
    roles: [UserRole.admin],
  },
};

export interface SidebarStructureNode {
  id: NavItemId;
  /**
   * Child item ids rendered as sub-items. When present, the parent renders
   * as a non-clickable group header (see `SidebarNavGroup`) and the actual
   * destinations live on the children.
   */
  children?: readonly NavItemId[];
}

export interface SidebarStructureGroup {
  group: NavGroupKey;
  nodes: readonly SidebarStructureNode[];
}

/**
 * Ordered structure of the desktop sidebar. Only ids live here — hrefs,
 * icons, labels and roles all come from `NAV_ITEMS`, so the sidebar cannot
 * drift from the other nav surfaces (bottom-nav, command palette).
 */
export const SIDEBAR_STRUCTURE: readonly SidebarStructureGroup[] = [
  { group: 'overview', nodes: [{ id: 'home' }, { id: 'foodWaste' }] },
  {
    group: 'dailyOperations',
    nodes: [
      { id: 'menuSelection' },
      { id: 'orders' },
      {
        id: 'menuManagement',
        children: ['menuPlanner', 'dishes', 'dishCategories', 'mealRatings'],
      },
      { id: 'distribution' },
    ],
  },
  {
    group: 'administration',
    nodes: [
      {
        id: 'employeeManagement',
        children: [
          'employeeManagementAll',
          'employeeManagementCreate',
          'employeeManagementBulk',
          'employeeOrders',
        ],
      },
      { id: 'companies' },
      { id: 'canteens' },
    ],
  },
];
